import type { TableChangesStorage } from '@/shared/components/data-grid/helpers'
import type {
	AnchorModel,
	AnchorsQueryModel,
	AnchorsQueryResponseModel,
} from '@/shared/generated'

import { createContext, use } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useDebounceValue } from 'usehooks-ts'

import {
	useAnchorsQuery,
	useDeleteAnchorsMutation,
	useUpsertAnchorsMutation,
} from '@/entities/anchor'

import { useTableChangesStorage } from '@/shared/components/data-grid/helpers'
import { confirm } from '@/shared/components/ui/confirmer'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { ampli } from '@/shared/lib/ampli'
import { useStore } from '@/shared/lib/datasheet/store'
import { useModals } from '@/shared/lib/modal-manager'
import { queryClient } from '@/shared/lib/query-client'
import {
	actDeleted,
	actUpdated,
	successActionMessage,
} from '@/shared/tools/message'

import { getTableRowId } from '../lib/helpers'

export type AnchorsManagementContextProps = {
	isLoading: boolean
	isError: boolean
	isFetching: boolean
	handleDeleteSelectedRows: () => void
	handleExportReportClick: () => void
	handleImportReportClick: () => void
	handleCreateAnchorSku: () => void
	handleSaveChanges: () => void
	handleRevertChanges: () => void
	anchors: AnchorsQueryResponseModel | undefined
	isDirty: boolean
	tableChangesStorage: TableChangesStorage<any>
}

export const AnchorsManagementContext =
	createContext<AnchorsManagementContextProps>({} as any)

export function AnchorsManagementProvider({
	children,
}: {
	children: React.ReactNode
}) {
	const { t } = useTranslation()
	const rowSelection = useStore((state) => state.rowSelection)
	const setRowSelection = useStore((state) => state.setRowSelection)
	const localData = useStore((state) => state.localData)
	const setLocalData = useStore((state) => state.setLocalData)

	const [{ limit, offset, filters, sorting, search }] = useLocationQuery()
	const { showModal } = useModals()
	const tableChangesStorage = useTableChangesStorage({
		setLocalData,
		localData,
		getId: getTableRowId,
	})
	const [searchTextValue] = useDebounceValue(search, 1000)
	const requestBody: AnchorsQueryModel = {
		limit,
		offset,
		filters,
		search: searchTextValue || undefined,
		order_by:
			sorting.map(({ id, desc }) => ({
				name: id,
				direction: desc ? 'desc' : 'asc',
			})) ?? [],
	}
	const anchorsQuery = useAnchorsQuery(requestBody, {
		placeholderData: (previousData) => previousData,
	})
	const deleteRelations = useDeleteAnchorsMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['anchor-assortment'],
			})
			queryClient.invalidateQueries({
				queryKey: ['anchors-relation-groups'],
			})
			setRowSelection({})
			toast.success(successActionMessage('Anchors', actDeleted(t), t), {
				id: 'anchor_deleting',
			})
		},
		onError: () => {
			toast.error(t('fatal_error_title'), {
				id: 'anchor_deleting',
			})
		},
	})
	const updateRelations = useUpsertAnchorsMutation({
		onSuccess: async () => {
			tableChangesStorage.reset()
			await queryClient.invalidateQueries({
				queryKey: ['anchor-assortment'],
			})
			await queryClient.invalidateQueries({
				queryKey: ['anchors-relation-groups'],
			})
			toast.success(successActionMessage('Anchors', actUpdated(t), t), {
				id: 'anchor_updating',
			})
		},
		onError: (error: any) => {
			if (error.response?.data.errors?.anchors) {
				const errors: any = Object.entries(
					error.response.data.errors?.anchors[0]
				)
				toast.error('Validation error', {
					description: `Field "${errors[0][0]}" has "${errors?.[0]?.[1]?.[0] || 0}"`,
					id: 'anchor_updating',
				})
			} else {
				toast.error(t('fatal_error_title'), {
					id: 'anchor_updating',
				})
			}
		},
	})
	const handleDeleteSelectedRows = () => {
		confirm({
			title: t('general_detete_confirm_title'),
			description: t('general_detete_confirm_subtitle'),
			actionText: t('general_delete'),
			cancelText: t('general_cancel'),
			ActionProps: {
				variant: 'destructive',
			},
		}).then((answer: boolean) => {
			if (answer) {
				ampli.anchoringDeleteSelectedRowsClick()
				toast.loading('Deleting anchors...', {
					id: 'anchor_deleting',
				})
				deleteRelations.mutate({
					anchors: Object.keys(rowSelection).map((item: string) => {
						const [sales_entity_id, sku, optimization_group_id] =
							item.split('__')

						return {
							sales_entity_id,
							sku,
							optimization_group_id: optimization_group_id || '',
						}
					}),
				})
			}
		})
	}
	const handleExportReportClick = () => {
		ampli.anchoringExportTableClick()
		showModal('EXPORT_ANCHORS_TABLE')
	}
	const handleImportReportClick = () => {
		ampli.anchoringImportFromFileClick()
		showModal('IMPORT_ANCHOR_ASSORTMENT_FROM_FILE')
	}
	const handleCreateAnchorSku = () => {
		ampli.anchoringTableCreateAnchor()
		showModal('CREATE_ANCHOR_SKU_MODAL')
	}
	const handleSaveChanges = () => {
		ampli.anchoringSaveManualChangesClick()
		toast.loading('Updating anchors...', {
			id: 'anchor_updating',
		})

		const changes = Object.entries(tableChangesStorage.getSnapshot()).map(
			([key, value]) => {
				const [sales_entity_id, sku, optimization_group_id] = key.split('__')
				const modifiedValue = Object.fromEntries(
					Object.entries(value).map(([fieldKey, fieldValue]) => [
						fieldKey,
						fieldValue === '' || fieldValue === '0' ? null : fieldValue,
					])
				)

				return {
					sales_entity_id,
					sku,
					optimization_group_id: optimization_group_id || '',
					...modifiedValue,
				}
			}
		)
		updateRelations.mutate({
			anchors: changes as AnchorModel[],
		})
	}
	const handleRevertChanges = () => {
		ampli.anchoringRevertManualChangesClick()
		tableChangesStorage.reset()
	}

	return (
		<AnchorsManagementContext
			value={{
				isLoading: true,
				isError: false,
				isFetching: anchorsQuery.isFetching,
				isDirty: tableChangesStorage.isDirty,
				handleDeleteSelectedRows,
				handleExportReportClick,
				handleImportReportClick,
				handleCreateAnchorSku,
				handleSaveChanges,
				handleRevertChanges,
				anchors: anchorsQuery.data,
				tableChangesStorage,
			}}
		>
			{children}
		</AnchorsManagementContext>
	)
}

export function useAnchorsManagementContext() {
	const context = use(AnchorsManagementContext)
	if (!context) {
		throw new Error(
			'useAnchorsManagementContext must be used within an AnchorsManagementProvider'
		)
	}

	return context
}
